/* Versionsanzeige und Update-Balken — für alle Vimaana-Web-Apps dieselbe.
 *
 * Zwei Bausteine, bewusst getrennt:
 *
 *   <MUpdateBalken />  oben in der Shell. Meldet sich nur, wenn wirklich eine
 *                      neuere Fassung ausgeliefert ist. Der Nutzer entscheidet,
 *                      wann er lädt — ein Neuladen mitten im Formular verlöre
 *                      Getipptes (Entscheidung Benjamin, 18.08.2026).
 *   <MVersionsZeile /> unten auf der Profil-/Einstellungsseite. Sagt immer,
 *                      welcher Stand läuft, und lässt von Hand nachsehen.
 *
 * Das eigene CSS wird einmal pro Seite eingehängt, damit die Komponente in
 * jeder App funktioniert, ohne dass dort eine Stildatei importiert werden muss.
 * Farben kommen über eine Fallback-Kette: Kontor & Co. benennen ihre Tokens
 * `--vg-*`, die Team-App `--accent`/`--text-1`. Wer keins von beidem hat,
 * bekommt den literalen Wert.
 */
import { useCallback, useEffect, useState } from "react";

import {
  buildText,
  laufenderBuild,
  neuerStandVerfuegbar,
  neueVersionLaden,
} from "../lib/version";

const STIL_ID = "vg-version-stil";

const STIL = `
.vg-updatebalken {
  display: flex; align-items: center; gap: .6rem; flex-wrap: wrap;
  padding: .6rem .9rem;
  padding-left: max(.9rem, env(safe-area-inset-left));
  padding-right: max(.9rem, env(safe-area-inset-right));
  background: var(--vg-accent-soft, var(--accent-soft, #e7f1ea));
  color: var(--vg-text-1, var(--text-1, #14241c));
  border-bottom: 1px solid var(--vg-accent-line, var(--accent-line, #b9d3c4));
  font-size: 14px; line-height: 1.35;
}
.vg-updatebalken .vg-ub-text { flex: 1 1 12rem; min-width: 0; }
.vg-updatebalken button {
  min-height: 44px; min-width: 44px;
  padding: .45rem 1rem; border-radius: 10px; border: 0;
  background: var(--vg-accent, var(--accent, #2f6f4f));
  color: #fff; font-size: 15px; font-weight: 600; cursor: pointer;
}
.vg-updatebalken button[disabled] { opacity: .65; cursor: default; }
.vg-updatebalken .vg-ub-spaeter {
  background: none; color: var(--vg-text-2, var(--text-2, #4a5b52));
  font-weight: 400; text-decoration: underline; padding: .45rem .5rem;
}
@media (max-width: 420px) {
  .vg-updatebalken { font-size: 13px; }
  .vg-updatebalken .vg-ub-text { flex-basis: 100%; }
}

.vg-versionszeile {
  text-align: center; font-size: 12px; line-height: 1.5;
  color: var(--vg-text-3, var(--text-3, #7a8a81));
  padding: .5rem 0 calc(.5rem + env(safe-area-inset-bottom));
}
.vg-versionszeile button {
  min-height: 44px; padding: .3rem .6rem;
  background: none; border: 0; cursor: pointer;
  color: var(--vg-accent, var(--accent, #2f6f4f));
  font-size: 12px; text-decoration: underline;
}
`;

function stilEinhaengen(): void {
  if (typeof document === "undefined" || document.getElementById(STIL_ID)) return;
  const el = document.createElement("style");
  el.id = STIL_ID;
  el.textContent = STIL;
  document.head.appendChild(el);
}

/** Wie oft im Hintergrund nachgesehen wird. Eine Viertelstunde reicht — die
 *  Prüfung kostet einen winzigen Abruf, soll aber kein Dauerfeuer sein. */
const PRUEF_ABSTAND_MS = 15 * 60 * 1000;

function usePruefung(): { veraltet: boolean; pruefen: () => Promise<boolean> } {
  const [veraltet, setVeraltet] = useState(false);

  const pruefen = useCallback(async () => {
    const neu = await neuerStandVerfuegbar();
    setVeraltet(neu);
    return neu;
  }, []);

  useEffect(() => {
    let lebt = true;
    const lauf = () => { void pruefen().then(() => lebt); };
    lauf();
    const uhr = window.setInterval(lauf, PRUEF_ABSTAND_MS);
    // Der wichtigste Moment: die App kommt nach Tagen aus dem Hintergrund
    // zurück. Genau dann ist sie am ehesten veraltet.
    const beiRueckkehr = () => { if (document.visibilityState === "visible") lauf(); };
    document.addEventListener("visibilitychange", beiRueckkehr);
    return () => {
      lebt = false;
      window.clearInterval(uhr);
      document.removeEventListener("visibilitychange", beiRueckkehr);
    };
  }, [pruefen]);

  return { veraltet, pruefen };
}

/**
 * Balken oben in der App — erscheint nur, wenn eine neuere Fassung bereitliegt.
 *
 * „Später" blendet ihn für diesen Seitenaufruf aus, nicht dauerhaft: beim
 * nächsten Öffnen ist er wieder da, solange der Stand alt ist. Ein dauerhaftes
 * Wegklicken wäre genau die Falle, die dieser Balken verhindern soll.
 */
export function MUpdateBalken() {
  const { veraltet } = usePruefung();
  const [versteckt, setVersteckt] = useState(false);
  const [laedt, setLaedt] = useState(false);

  useEffect(stilEinhaengen, []);
  if (!veraltet || versteckt) return null;

  return (
    <div className="vg-updatebalken" role="status">
      <span className="vg-ub-text">Es gibt eine neuere Fassung dieser App.</span>
      <button
        type="button"
        disabled={laedt}
        onClick={() => { setLaedt(true); void neueVersionLaden(); }}
      >
        {laedt ? "Lädt…" : "Jetzt laden"}
      </button>
      <button type="button" className="vg-ub-spaeter" onClick={() => setVersteckt(true)}>
        Später
      </button>
    </div>
  );
}

/**
 * Zeile für die Profil-/Einstellungsseite: welcher Stand läuft hier gerade.
 *
 * Der Knopf sucht auf Wunsch sofort nach, statt auf die Viertelstunde zu warten
 * — das ist der Griff für den Fall „bei mir sieht es anders aus als bei dir".
 */
export function MVersionsZeile({ name }: { name?: string }) {
  const [zustand, setZustand] = useState<"" | "sucht" | "aktuell" | "veraltet">("");
  const { pruefen } = usePruefung();
  const build = laufenderBuild();

  useEffect(stilEinhaengen, []);

  const nachsehen = async () => {
    setZustand("sucht");
    setZustand((await pruefen()) ? "veraltet" : "aktuell");
  };

  return (
    <div className="vg-versionszeile">
      <div>{name ? `${name} · ` : ""}Stand {buildText(build)}</div>
      <button type="button" onClick={() => void nachsehen()}>
        {zustand === "sucht" ? "sucht…" : "Nach Updates suchen"}
      </button>
      {zustand === "aktuell" && <div>Du hast den aktuellen Stand.</div>}
      {zustand === "veraltet" && (
        <div>
          Es gibt etwas Neueres.{" "}
          <button type="button" onClick={() => void neueVersionLaden()}>Jetzt laden</button>
        </div>
      )}
    </div>
  );
}
